import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {
  isAntibodyImplementedInterface,
  isCompleteImplementedAntibodyInterface,
  requeryAntibodyConfigurationTable,
} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import {assert} from 'Asserts';
import {ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT} from 'auth/DefaultSchemaCollaboratorsConfiguration';
import * as BenchlingUtil from 'BenchlingUtil';
import {useSchemaCollaboratorsSectionWithDefaults} from 'collaborator-components/useSchemaCollaboratorsSection';
import {SavingState} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  isCompleteImplementedComplexPolymerInterface,
  isComplexPolymerImplementedInterface,
} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import {requeryCreateMenuTagSchemas} from 'nav/navbar/CreateMenu';
import notifications from 'notifications';
import {addTagSchemaToFieldTableQueryCache, ClientSchemaField} from 'registry/field/FieldTable';
import * as RegistryUtil from 'registry/RegistryUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {
  InterfaceConstraintErrorDisplayDataForSchema,
  getInterfaceConstraintErrorsForSchema,
} from 'schemas/schema-interface/validation/SchemaInterfaceValidationUtils';
import SchemaSettingsPageContainer from 'schemas/SchemaSettingsPageContainer';
import {addTagSchemaToTagSchemaSelectorQueryCache} from 'schemas/shared/TagSchemaOrInterfaceSelector';
import {addTagSchemaToTagSchemaSummariesQueryCache} from 'schemas/useTagSchemaSummariesListingQuery';
import TagSchemaFileSettings, {
  CREATE_FILE_SETTINGS_LOCAL_PARAM_KEYS,
  CreateFileSettingParams,
  useCreateSchemaParams,
} from 'settings/registry/TagSchemaFileSettings';
import {TagSchemaPermissionSettings} from 'settings/registry/TagSchemaPermissionSettings';
import * as TagSchemaActions from 'tag-schema/TagSchemaActions';
import {useTagSchemaSavingState} from 'tag-schema/TagSchemaClientState';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import StepNav, {StepItemStatus} from 'ui-library/StepNav';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import licenses from 'user/Licenses';
import {isServerRequestError} from 'util/Net';
import {omit} from 'util/omitKey';
import {withLoadingScreen} from 'util/Promises';

import {
  TagSchemaCreateFlowRegistryOwnerQuery,
  TagSchemaCreateFlowRegistryOwnerQueryVariables,
} from './__generated/TagSchemaCreateFlowRegistryOwnerQuery';

type TagSchemaCreateFlowRegistryOwnerQueryOrganization = ExtractByTypename<
  TagSchemaCreateFlowRegistryOwnerQuery['registry'],
  'RegistryNode'
>['organization'];

export enum TagSchemaCreateStepKey {
  FILE = 'FILE',
  ACCESS_POLICIES = 'ACCESS_POLICIES',
}

export interface Props {
  ownerUrlId: string;
  registryId: string;
}

interface QueryingProps {
  organization: TagSchemaCreateFlowRegistryOwnerQueryOrganization;
}

export const TAG_SCHEMA_CREATE_FLOW_REGISTRY_OWNER_QUERY = gql`
  query TagSchemaCreateFlowRegistryOwnerQuery($registryId: StrictString!)
  @skipAudit
  @engComponent(name: REGISTRY) {
    registry(id: $registryId) {
      ... on RegistryNode {
        id
        organization {
          ... on OrganizationNode {
            ...OrganizationDefaultSchemaCollaboratorsConfiguration
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT}
`;

function verifyFields(fields: Array<SchemaField | ClientSchemaField>): boolean {
  for (const field of fields) {
    if (_.where(fields, {name: field.name}).length > 1) {
      notifications.error(`You may not add two fields with the same name: ${field.name}`);
      return false;
    }
  }
  return true;
}

function hasRequiredFileFields(fileSettingParams: CreateFileSettingParams): boolean {
  return (
    fileSettingParams.prefix.trim().length > 0 &&
    fileSettingParams.name.trim().length > 0 &&
    fileSettingParams.itemType != null &&
    fileSettingParams.labelingStrategies != null &&
    fileSettingParams.labelingStrategies.length > 0 &&
    fileSettingParams.implementedStaticSchemaInterfaces.every((issi) => {
      if (isComplexPolymerImplementedInterface(issi)) {
        // If this is a complex polymer interface, it must be complete.
        return isCompleteImplementedComplexPolymerInterface(issi);
      } else if (isAntibodyImplementedInterface(issi)) {
        // Same for antibodies.
        return isCompleteImplementedAntibodyInterface(issi);
      }
      // All other exclusive SSIs do not require any additional information for schema config.
      return true;
    })
  );
}

function TagSchemaCreateFlow(props: Props & QueryingProps): JSX.Element {
  const [saving] = useTagSchemaSavingState();

  React.useEffect(() => {
    if (RegistryUtil.isSavingFinished(saving)) {
      TagSchemaActions.resetSavingState();
      if (saving === SavingState.CREATE_SUCCESS) {
        BenchlingUtil.navigate(
          null,
          RegistryUtil.getRegistrySchemasSettingsUrl(props.ownerUrlId, props.registryId)
        );
      }
    }
  }, [props.ownerUrlId, props.registryId, saving]);

  const [currentStepKey, setCurrentStep] = React.useState(TagSchemaCreateStepKey.FILE);
  const [{fileSettingParams}, setCreateSchemaParams] = useCreateSchemaParams();
  const [interfaceConstraintErrors, setInterfaceConstraintErrors] = React.useState<
    Array<InterfaceConstraintErrorDisplayDataForSchema>
  >([]);

  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSectionWithDefaults(
    props.organization
  );

  const steps = _.compact([
    {
      key: TagSchemaCreateStepKey.FILE,
      title: 'Entity Settings',
      isNextOrCreateButtonEnabled: (): boolean => hasRequiredFileFields(fileSettingParams),
      validateFields: (): boolean => fileSettingParams != null && verifyFields(fileSettingParams.fields),
    },
    {
      key: TagSchemaCreateStepKey.ACCESS_POLICIES,
      title: 'Access Policies',
      isNextOrCreateButtonEnabled: (): boolean => collaborators.length > 0,
      validateFields: (): boolean => true,
    },
  ]);
  const currentStepIndex = steps.findIndex((step) => step.key === currentStepKey);

  function handleClickSchemas(): void {
    BenchlingUtil.navigate(
      null,
      RegistryUtil.getRegistrySchemasSettingsUrl(props.ownerUrlId, props.registryId)
    );
  }

  async function handleCreateSchema(): Promise<void> {
    if (!fileSettingParams) {
      return;
    }

    const fileSettingParamsToSend = omit(fileSettingParams, ...CREATE_FILE_SETTINGS_LOCAL_PARAM_KEYS);
    if (fileSettingParamsToSend.itemType == null) {
      return;
    }

    const createParameters = {
      ...fileSettingParamsToSend,
      itemType: fileSettingParamsToSend.itemType,
      fields: fileSettingParams.fields.filter((field) => !field.isComputed),
      registryId: props.registryId,
      collaborators: minimalCollaborators,
    };

    await withLoadingScreen(
      (async () => {
        try {
          const tagSchema = await TagSchemaActions.create(createParameters);
          if (tagSchema != null) {
            addTagSchemaToFieldTableQueryCache(tagSchema, props.registryId, props.organization.id);
            addTagSchemaToTagSchemaSelectorQueryCache(tagSchema);
            addTagSchemaToTagSchemaSummariesQueryCache(tagSchema, props.registryId);
            if (isBiologicsEnabled()) {
              await requeryAntibodyConfigurationTable();
            }
            requeryCreateMenuTagSchemas();
          }
        } catch (e) {
          const constraintErrors = getInterfaceConstraintErrorsForSchema(e.responseJSON);
          if (constraintErrors != null) {
            setInterfaceConstraintErrors(constraintErrors);
            setCurrentStep(steps[0].key);
          } else if (isServerRequestError(e)) {
            // Normally, we would `notifications.notifyError(e)` or `throw e` here, to show an error
            // notification to the user. However, `TagSchemaActions.create` already does that for us. So we
            // no-op.
            noop();
          } else {
            throw e;
          }
        }
      })(),
      'Saving...'
    );
  }

  return (
    <SchemaSettingsPageContainer
      onNavigateToListing={handleClickSchemas}
      schema={null}
      schemaTypeName="Entity Schema"
      showExport={licenses.hasEnterprisePlan()}
    >
      <div className="u-flex-grow" {...getTestProps({component: 'RegistrySettingsPage', element: 'root'})}>
        <HeaderNavigation steps={steps} currentStepKey={currentStepKey} setCurrentStep={setCurrentStep}>
          <>
            {currentStepKey === TagSchemaCreateStepKey.FILE && (
              <TagSchemaFileSettings
                ownerUrlId={props.ownerUrlId}
                registryId={props.registryId}
                tagSchemaId={null}
                createSchemaParams={{fileSettingParams}}
                setCreateSchemaParams={setCreateSchemaParams}
                interfaceConstraintErrors={interfaceConstraintErrors}
              />
            )}
            {currentStepKey === TagSchemaCreateStepKey.ACCESS_POLICIES && (
              <TagSchemaPermissionSettings
                isReadOnly={false}
                collaborators={collaborators}
                onUpdate={handleUpdate}
              />
            )}
          </>
        </HeaderNavigation>
        <div className={css(styles.buttonContainer)}>
          {currentStepIndex !== 0 && (
            <Button
              buttonStyle={ButtonStyle.LINK}
              text="Back"
              className="u-small-spacing-left"
              onClick={() => {
                setCurrentStep(steps[currentStepIndex - 1].key);
              }}
            />
          )}
          {currentStepIndex === steps.length - 1 ? (
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.MEDIUM}
              text="Create"
              className="u-small-spacing-left"
              disabled={!steps[currentStepIndex].isNextOrCreateButtonEnabled()}
              onClick={() => {
                if (!steps[currentStepIndex].validateFields()) {
                  return;
                }
                handleCreateSchema();
              }}
              dataTestAttrs={{component: 'TagSchemaCreateFlow', element: 'createButton'}}
            />
          ) : (
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.MEDIUM}
              text="Next"
              className="u-small-spacing-left"
              disabled={!steps[currentStepIndex].isNextOrCreateButtonEnabled()}
              onClick={() => {
                if (!steps[currentStepIndex].validateFields()) {
                  return;
                }
                setCurrentStep(steps[currentStepIndex + 1].key);
              }}
              dataTestAttrs={{component: 'TagSchemaCreateFlow', element: 'nextButton'}}
            />
          )}
        </div>
      </div>
    </SchemaSettingsPageContainer>
  );
}

interface HeaderNavigationProps {
  steps: Array<{key: TagSchemaCreateStepKey; title: string}>;
  currentStepKey: TagSchemaCreateStepKey;
  setCurrentStep: (key: TagSchemaCreateStepKey) => void;
  children: JSX.Element;
}

function HeaderNavigation(props: HeaderNavigationProps): JSX.Element {
  if (props.steps.length === 1) {
    return (
      <TabContainer
        activeTab={props.currentStepKey}
        className="u-flex-grow"
        onRequestTabChange={props.setCurrentStep}
      >
        <Tab tabKey={props.steps[0].key} title={props.steps[0].title}>
          {props.children}
        </Tab>
      </TabContainer>
    );
  }
  return (
    <div>
      <StepNav<TagSchemaCreateStepKey>
        steps={props.steps}
        statusByStep={{
          [TagSchemaCreateStepKey.FILE]: StepItemStatus.UNVISITED,
          [TagSchemaCreateStepKey.ACCESS_POLICIES]: StepItemStatus.UNVISITED,
        }}
        currentStepKey={props.currentStepKey}
        onUpdateCurrentStep={props.setCurrentStep}
      />
      {props.children}
    </div>
  );
}

export default function QueryingTagSchemaCreateFlow(props: Props): JSX.Element | null {
  const registryOwnerResult = useBenchlingQuery<
    TagSchemaCreateFlowRegistryOwnerQuery,
    TagSchemaCreateFlowRegistryOwnerQueryVariables
  >(TAG_SCHEMA_CREATE_FLOW_REGISTRY_OWNER_QUERY, {
    variables: {registryId: props.registryId},
  });

  if (registryOwnerResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (registryOwnerResult.type === QueryResultType.ERROR) {
    return null;
  }

  assert(!isInaccessible(registryOwnerResult.data.registry), 'Expecting registry to be accessible');
  return <TagSchemaCreateFlow {...props} organization={registryOwnerResult.data.registry.organization} />;
}

const styles = StyleSheet.create({
  buttonContainer: {
    float: 'right',
  },
});
